import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Container, Grid, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import Footer from '../components/Footer';
import QuoteForm from '../components/QuoteForm';
import Seo from '../components/Seo';
import Section from '../components/ui/Section';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import IconCard from '../components/ui/IconCard';
import CtaPanel from '../components/ui/CtaPanel';
import TrustBadges from '../components/ui/TrustBadges';
import ReviewCard from '../components/ui/ReviewCard';
import { CallButton, GetQuoteButton } from '../components/ui/QuoteButtons';
import { fadeInUp } from '../components/ui/motion';
import { getLatestReviews } from '../data/realGoogleReviews';
import { localBusinessSchema, websiteSchema, SERVICE_AREAS } from '../constants/seo';
import { business, colors, layout, radii } from '../theme/tokens';
import { photos } from '../utils/images';

const heroSlides = [
  { image: photos.kitchen, alt: 'Completed high-end kitchen renovation in Kingsbury by Behson Property Solutions' },
  { image: photos.bathroom, alt: 'Modern bathroom renovation with walk-in shower completed in NW9' },
  { image: photos.outdoor, alt: 'Exterior building and roofing work on a North West London home' },
  { image: photos.garden, alt: 'Garden landscaping and patio construction completed by Behson' },
];

const services = [
  {
    title: 'Plumbing',
    path: '/plumbing',
    cta: 'Plumber in Kingsbury',
    image: photos.bathroom2,
    alt: 'Newly installed bathroom plumbing and fixtures in Kingsbury',
    description: 'Repairs, installations, leak detection and drain work for homes in Kingsbury and NW9.',
  },
  {
    title: 'Renovations',
    path: '/renovations',
    cta: 'Kitchen & bathroom renovations',
    image: photos.kitchen,
    alt: 'Fully renovated kitchen with island and integrated appliances',
    description: 'Kitchen renovation, bathroom renovation and whole-house refurbishment, managed end to end.',
  },
  {
    title: 'Maintenance',
    path: '/maintenance',
    cta: 'Property maintenance NW9',
    image: photos.roof2,
    alt: 'Roof repair and property maintenance work in North West London',
    description: 'Roofing, repairs and ongoing property care so small jobs never become big ones.',
  },
];

const recentWork = [
  { image: photos.kitchenIsland, alt: 'Kitchen island with quartz worktop fitted in a Kingsbury kitchen renovation' },
  { image: photos.bathroom3, alt: 'Tiled bathroom renovation with modern vanity unit' },
  { image: photos.roof, alt: 'New roof tiles installed on a residential property in NW9' },
  { image: photos.shower, alt: 'Walk-in shower enclosure installed during a bathroom refit' },
  { image: photos.wardrobe, alt: 'Bespoke fitted wardrobes installed in a bedroom renovation' },
  { image: photos.outdoor2, alt: 'Rear house extension and exterior renovation in North West London' },
];

const whyCards = [
  {
    icon: <VerifiedUserOutlinedIcon />,
    title: 'Licensed, insured and local',
    description: 'A Kingsbury-based team you can call back. Fully insured, with 25+ years of hands-on building and plumbing experience.',
  },
  {
    icon: <HandymanOutlinedIcon />,
    title: 'One team for the whole job',
    description: 'Plumbing, tiling, carpentry, roofing and finishing under one roof — no juggling separate trades or quotes.',
  },
  {
    icon: <ScheduleOutlinedIcon />,
    title: 'Clear quotes, tidy work',
    description: 'Straightforward pricing, agreed timelines and a clean site at the end of every day.',
  },
  {
    icon: <PhotoCameraOutlinedIcon />,
    title: 'Quote from your photos',
    description: 'Attach photos to the quote form and we can give you an initial assessment before a site visit.',
  },
];

const Home = () => {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = heroSlides[(slide + 1) % heroSlides.length].image;
      const img = new Image();
      img.srcset = next.srcSet;
      img.sizes = '100vw';
      img.src = next.src;
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [slide]);

  const reviews = getLatestReviews(3);

  return (
    <Box sx={{ backgroundColor: colors.bg }}>
      <Seo
        title="Plumber & Renovation Builders in Kingsbury, NW9"
        description="Behson Property Solutions: local plumber, kitchen & bathroom renovation and property maintenance in Kingsbury, NW9, Colindale and Edgware. Licensed, insured, free quotes."
        path="/"
        noSuffix={false}
        schema={[localBusinessSchema, websiteSchema]}
      />

      {/* Hero */}
      <Box
        component="header"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: 640, md: '88vh' },
          display: 'flex',
          alignItems: 'center',
          mt: `-${layout.navHeight}px`,
          pt: `${layout.navHeight}px`,
        }}
      >
        <AnimatePresence initial={false}>
          <motion.div
            key={slide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: 'easeInOut' }}
            style={{ position: 'absolute', inset: 0 }}
          >
            <Box
              component="img"
              src={heroSlides[slide].image.src}
              srcSet={heroSlides[slide].image.srcSet}
              sizes="100vw"
              alt={heroSlides[slide].alt}
              loading="eager"
              fetchPriority={slide === 0 ? 'high' : 'auto'}
              decoding="async"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </motion.div>
        </AnimatePresence>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(17,17,17,0.92) 0%, rgba(17,17,17,0.72) 45%, rgba(17,17,17,0.35) 100%), linear-gradient(180deg, rgba(17,17,17,0.2) 0%, rgba(17,17,17,0) 30%, rgba(17,17,17,0.85) 100%)',
          }}
        />

        <Container sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 10 } }}>
          <Box sx={{ maxWidth: 720 }}>
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
              <Typography variant="overline" component="p" sx={{ color: colors.accent, mb: 2 }}>
                Kingsbury · NW9 · North West London
              </Typography>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} transition={{ delay: 0.05 }}>
              <Typography
                component="h1"
                variant="h1"
                sx={{ color: colors.white, fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.2rem' } }}
              >
                Plumbing, renovations &amp; property maintenance in Kingsbury
              </Typography>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} transition={{ delay: 0.15 }}>
              <Typography sx={{ color: colors.textMuted, mt: 3, fontSize: { xs: '1.05rem', md: '1.25rem' }, maxWidth: 600 }}>
                A licensed, insured local team with 25+ years&apos; experience — from urgent plumbing repairs to full kitchen
                and bathroom renovations across NW9, Colindale and Edgware.
              </Typography>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} transition={{ delay: 0.25 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 4 }}>
                <GetQuoteButton />
                <CallButton />
              </Box>
            </motion.div>
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} transition={{ delay: 0.35 }}>
              <Box sx={{ mt: 5 }}>
                <TrustBadges />
              </Box>
            </motion.div>
          </Box>
        </Container>

        {/* Slide indicators */}
        <Box
          role="group"
          aria-label="Hero image slides"
          sx={{ position: 'absolute', right: { xs: 16, md: 40 }, bottom: { xs: 20, md: 36 }, display: 'flex', gap: 1, zIndex: 1 }}
        >
          {heroSlides.map((s, i) => (
            <Box
              key={s.alt}
              component="button"
              type="button"
              aria-label={`Show slide ${i + 1}`}
              aria-pressed={i === slide}
              onClick={() => setSlide(i)}
              sx={{
                width: i === slide ? 28 : 8,
                height: 8,
                p: 0,
                borderRadius: radii.pill,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: i === slide ? colors.white : 'rgba(255,255,255,0.4)',
                transition: 'width .3s ease, background-color .3s ease',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Services */}
      <Section tone="alt" id="services">
        <SectionHeading
          eyebrow="What we do"
          title="Building, plumbing and maintenance services in NW9"
          lede="Three core services, one accountable team. Pick a service to see what's included and how we work."
        />
        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={service.path}>
              <Reveal delay={index * 0.08} style={{ height: '100%' }}>
                <Box
                  component={RouterLink}
                  to={service.path}
                  aria-label={`${service.title}: ${service.cta}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    textDecoration: 'none',
                    borderRadius: radii.lg,
                    overflow: 'hidden',
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    transition: 'transform .3s ease, border-color .3s ease',
                    '&:hover': { transform: 'translateY(-6px)', borderColor: colors.borderStrong },
                    '&:hover img': { transform: 'scale(1.05)' },
                    '&:hover .cta svg': { transform: 'translateX(4px)' },
                  }}
                >
                  <Box sx={{ position: 'relative', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={service.image.src}
                      srcSet={service.image.srcSet}
                      sizes="(max-width: 900px) 100vw, 33vw"
                      alt={service.alt}
                      loading="lazy"
                      decoding="async"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .6s ease' }}
                    />
                  </Box>
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography component="h3" variant="h4" sx={{ color: colors.text, fontSize: '1.45rem' }}>
                      {service.title}
                    </Typography>
                    <Typography sx={{ color: colors.textMuted, mt: 1, mb: 3, flex: 1 }}>{service.description}</Typography>
                    <Typography
                      className="cta"
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: colors.white, fontWeight: 600, '& svg': { fontSize: 18, transition: 'transform .2s ease' } }}
                    >
                      {service.cta} <ArrowForwardIcon />
                    </Typography>
                  </Box>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Recent work */}
      <Section compact>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <SectionHeading eyebrow="Recent work" title="Projects completed across North West London" mb={0} />
          <Button component={RouterLink} to="/gallery" variant="text" endIcon={<ArrowForwardIcon />}>
            View the full gallery
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
          }}
        >
          {recentWork.map((item) => (
            <Box
              key={item.alt}
              component={RouterLink}
              to="/gallery"
              aria-label={item.alt}
              sx={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: radii.md, overflow: 'hidden', display: 'block', '&:hover img': { transform: 'scale(1.06)' } }}
            >
              <Box
                component="img"
                src={item.image.src}
                srcSet={item.image.srcSet}
                sizes="(max-width: 600px) 50vw, (max-width: 900px) 33vw, 16vw"
                alt={item.alt}
                loading="lazy"
                decoding="async"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .6s ease' }}
              />
            </Box>
          ))}
        </Box>
      </Section>

      {/* Quote form */}
      <Section tone="alt" id="quote-form" component="div">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <SectionHeading
              eyebrow="Free quote"
              title="Tell us about the job"
              lede="Send a few details and photos and we'll come back to you within 24 hours with next steps — often with an initial price before a site visit."
              mb={4}
            />
            <Reveal>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2, color: colors.textMuted }}>
                {[
                  'Free, no-obligation quotes for homes across Kingsbury & NW9',
                  'Attach up to 5 photos to speed up your assessment',
                  'Response within 24 hours, usually much sooner',
                ].map((line) => (
                  <Box component="li" key={line} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.white, mt: '0.6em', flexShrink: 0 }} />
                    {line}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: colors.textMuted, mt: 4 }}>
                Prefer to talk?{' '}
                <Box component="a" href={business.phoneHref} sx={{ color: colors.white, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  Call {business.phoneDisplay}
                </Box>{' '}
                or message us on{' '}
                <Box component="a" href={business.whatsappHref} target="_blank" rel="noopener noreferrer" sx={{ color: colors.white, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  WhatsApp
                </Box>
                .
              </Typography>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={7}>
            <Reveal>
              <QuoteForm />
            </Reveal>
          </Grid>
        </Grid>
      </Section>

      {/* Reviews */}
      <Section>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 5 }}>
          <SectionHeading eyebrow="Google reviews" title="What homeowners in Kingsbury say" mb={0} />
          <Button component={RouterLink} to="/testimonials" variant="text" endIcon={<ArrowForwardIcon />}>
            Read all reviews
          </Button>
        </Box>
        <Grid container spacing={3}>
          {reviews.map((review, index) => (
            <Grid item xs={12} md={4} key={review.author_name}>
              <Reveal delay={index * 0.08} style={{ height: '100%' }}>
                <ReviewCard review={review} />
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* About teaser */}
      <Section tone="alt">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Reveal>
              <Box sx={{ position: 'relative', borderRadius: radii.xl, overflow: 'hidden', aspectRatio: '4 / 3', border: `1px solid ${colors.border}` }}>
                <Box
                  component="img"
                  src={photos.kitchenStove.src}
                  srcSet={photos.kitchenStove.srcSet}
                  sizes="(max-width: 900px) 100vw, 50vw"
                  alt="Range cooker and splashback fitted during a kitchen renovation by Behson"
                  loading="lazy"
                  decoding="async"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </Box>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionHeading eyebrow="About Behson" title="25+ years of hands-on building experience" mb={3} />
            <Reveal>
              <Typography sx={{ color: colors.textMuted, mb: 2 }}>
                We&apos;ve worked on everything from emergency leaks to complete home renovations. Whether it&apos;s plumbing
                installations, kitchen and bathroom remodels, roofing or general maintenance, every job gets the same
                attention to detail.
              </Typography>
              <Typography sx={{ color: colors.textMuted, mb: 4 }}>
                We&apos;re based in Kingsbury and work across North West London, so we can be on site quickly and come back
                if you ever need us.
              </Typography>
              <Button component={RouterLink} to="/about" variant="outlined" size="large" endIcon={<ArrowForwardIcon />}>
                About the team
              </Button>
            </Reveal>
          </Grid>
        </Grid>
      </Section>

      {/* Why choose us */}
      <Section>
        <SectionHeading eyebrow="Why Behson" title="Why homeowners choose us" align="center" />
        <Grid container spacing={3}>
          {whyCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={card.title}>
              <Reveal delay={index * 0.06} style={{ height: '100%' }}>
                <IconCard {...card} />
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Areas */}
      <Section tone="alt" compact>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={5}>
            <SectionHeading eyebrow="Where we work" title="Serving Kingsbury and North West London" mb={2} />
            <Reveal>
              <Typography sx={{ color: colors.textMuted }}>
                Based in NW9, we regularly work across these areas.{' '}
                <Box component={RouterLink} to="/locations" sx={{ color: colors.white, fontWeight: 600 }}>
                  See all service areas
                </Box>
                .
              </Typography>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={7}>
            <Reveal>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {SERVICE_AREAS.map((area) => (
                  <Chip
                    key={area}
                    label={area}
                    component={RouterLink}
                    to="/locations"
                    clickable
                    icon={<PlaceOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text, px: 0.5, py: 2.2, fontSize: '0.95rem', '&:hover': { borderColor: colors.white } }}
                  />
                ))}
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Section>

      <Section compact>
        <CtaPanel
          title="Ready to start? Get a free quote today."
          text="Send photos and a short description through the form, or call us — we'll advise on the best next step for your home."
        />
      </Section>

      <Footer />
    </Box>
  );
};

export default Home;
